import { Button, Dialog } from "@mui/material";
import { Field, FormikProvider, useFormik } from "formik";
import { QRCodeSVG } from "qrcode.react";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";

import { InputField } from "components/InputField";
import {
  disableTwoFactor,
  getTwoFactorStatus,
  setupTwoFactor,
  verifyTwoFactorSetup,
} from "services/TwoFactorService";

import styles from "./TwoFactorSection.module.scss";
import SlapshotSwitch from "../../components/SlapshotSwitch";

type SetupStep = "qr" | "recovery";

const TwoFactorSection: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean | null>(null);

  const [setupOpen, setSetupOpen] = useState(false);
  const [setupStep, setSetupStep] = useState<SetupStep>("qr");
  const [provisioningUri, setProvisioningUri] = useState("");
  const [secret, setSecret] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [setupError, setSetupError] = useState<string | null>(null);

  const [disableOpen, setDisableOpen] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  useEffect(() => {
    getTwoFactorStatus()
      .then((res) => setEnabled(res.enabled))
      .catch(() => setEnabled(false));
  }, []);

  const startSetup = () => {
    setSetupError(null);
    setSetupStep("qr");
    setupTwoFactor()
      .then((res) => {
        setProvisioningUri(res.provisioning_uri);
        setSecret(res.secret);
        setSetupOpen(true);
      })
      .catch((err) => {
        setSetupError(err?.detail || "Could not start 2FA setup");
      });
  };

  const verifyFormik = useFormik({
    initialValues: { otp: "" },
    validationSchema: Yup.object({
      otp: Yup.string().trim().required("Code is required"),
    }),
    onSubmit: (values, { setStatus, resetForm }) => {
      verifyTwoFactorSetup(values.otp.trim())
        .then((res) => {
          setRecoveryCodes(res.recovery_codes);
          setSetupStep("recovery");
          setEnabled(true);
          resetForm();
        })
        .catch((err) => {
          setStatus(err?.detail || "Invalid code");
        });
    },
  });

  const disableFormik = useFormik({
    initialValues: { password: "", otp: "" },
    validationSchema: Yup.object({
      password: Yup.string().required("Password is required"),
      otp: Yup.string().trim().required("Code is required"),
    }),
    onSubmit: (values, { setStatus, resetForm }) => {
      disableTwoFactor(values.password, values.otp.trim())
        .then(() => {
          setEnabled(false);
          setDisableOpen(false);
          resetForm();
        })
        .catch((err) => {
          setStatus(err?.detail || "Could not disable 2FA");
        });
    },
  });

  const handleToggle = () => {
    if (enabled === null) return;
    if (enabled) {
      disableFormik.resetForm();
      disableFormik.setStatus(undefined);
      setDisableOpen(true);
    } else {
      startSetup();
    }
  };

  const closeSetup = () => {
    setSetupOpen(false);
    setSetupStep("qr");
    setProvisioningUri("");
    setSecret("");
    setRecoveryCodes([]);
    setSecretCopied(false);
    verifyFormik.resetForm();
    verifyFormik.setStatus(undefined);
  };

  const copySecret = () => {
    if (!secret) return;
    navigator.clipboard
      .writeText(secret)
      .then(() => {
        setSecretCopied(true);
        window.setTimeout(() => setSecretCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard may be blocked (e.g. non-HTTPS, denied permission).
        // The text is `user-select: all`, so the user can still select+copy.
      });
  };

  return (
    <div className={styles.section}>
      <div className={styles.row}>
        <div className={styles.label}>
          <h4>Two-Factor Authentication</h4>
          <p>
            {enabled
              ? "Two-factor authentication is on. You'll be asked for a 6-digit code at every login."
              : "Add an extra layer of security by requiring a code from your authenticator app at login."}
          </p>
        </div>
        <SlapshotSwitch
          id="two-factor-switch"
          checked={!!enabled}
          onChange={handleToggle}
          labelClassName={styles.switchLabel}
        />
      </div>
      {setupError && <p className={styles.error}>{setupError}</p>}

      <Dialog
        open={setupOpen}
        onClose={closeSetup}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "#16181e",
              color: "#ffffff",
              borderRadius: "16px",
            },
          },
        }}
      >
        <div className={styles.faDialog}>
          {setupStep === "qr" && (
            <>
              <h3>Set up two-factor authentication</h3>
              <p>
                Scan this QR code in your authenticator app (Google
                Authenticator, 1Password, Authy, etc.). Then enter the 6-digit
                code it shows to confirm.
              </p>
              <div className={styles.qrBlock}>
                {provisioningUri && (
                  <QRCodeSVG
                    value={provisioningUri}
                    size={192}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    includeMargin
                  />
                )}
              </div>
              <p className={styles.secretLabel}>
                Can&apos;t scan? Enter this code manually:
              </p>
              <div className={styles.secretRow}>
                <code className={styles.secret}>{secret}</code>
                <Button
                  className={styles.copyButton}
                  onClick={copySecret}
                  disabled={!secret}
                >
                  {secretCopied ? "Copied" : "Copy"}
                </Button>
              </div>
              <FormikProvider value={verifyFormik}>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    verifyFormik.handleSubmit();
                  }}
                  className={styles.form}
                >
                  {verifyFormik.status && (
                    <p className={styles.error}>{verifyFormik.status}</p>
                  )}
                  <Field
                    name="otp"
                    component={InputField}
                    label="Authentication code"
                    placeholder="123456"
                    autoComplete="one-time-code"
                  />
                  <div className={styles.actions}>
                    <Button className="noBackgroundButton" onClick={closeSetup}>
                      Cancel
                    </Button>
                    <Button
                      className="slapShotButton"
                      onClick={() => verifyFormik.handleSubmit()}
                    >
                      Verify &amp; enable
                    </Button>
                  </div>
                </form>
              </FormikProvider>
            </>
          )}
          {setupStep === "recovery" && (
            <>
              <h3>Save your recovery codes</h3>
              <p>
                Each code can be used once to sign in if you lose access to your
                authenticator app. Save them somewhere safe &mdash; you
                won&apos;t see them again.
              </p>
              <div className={styles.recoveryBlock}>
                {recoveryCodes.map((c) => (
                  <code key={c}>{c}</code>
                ))}
              </div>
              <div className={styles.actions}>
                <Button
                  className="noBackgroundButton"
                  onClick={() => {
                    navigator.clipboard.writeText(recoveryCodes.join("\n"));
                  }}
                >
                  Copy all
                </Button>
                <Button className="slapShotButton" onClick={closeSetup}>
                  I&apos;ve saved these
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>

      <Dialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "#16181e",
              color: "#ffffff",
              borderRadius: "16px",
            },
          },
        }}
      >
        <div className={styles.faDialog}>
          <h3>Turn off two-factor authentication</h3>
          <p>
            Enter your password and a current authenticator code (or a recovery
            code) to confirm.
          </p>
          <FormikProvider value={disableFormik}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                disableFormik.handleSubmit();
              }}
              className={styles.form}
            >
              {disableFormik.status && (
                <p className={styles.error}>{disableFormik.status}</p>
              )}
              <Field
                name="password"
                component={InputField}
                label="Password"
                type="password"
              />
              <Field
                name="otp"
                component={InputField}
                label="Authentication or recovery code"
                placeholder="123456"
                autoComplete="one-time-code"
              />
              <div className={styles.actions}>
                <Button
                  className="noBackgroundButton"
                  onClick={() => setDisableOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="slapShotButton"
                  onClick={() => disableFormik.handleSubmit()}
                >
                  Turn off
                </Button>
              </div>
            </form>
          </FormikProvider>
        </div>
      </Dialog>
    </div>
  );
};

export default TwoFactorSection;
